// SOR-618: session stats for one live stream — the back face of the stats
// flip, and a standalone panel on the watch page (the flip is an
// affordance, never the only route to the data).
//
// Fetches the ViewerCount series once on mount; StatsFlip lazy-mounts this,
// so nothing fires while someone scans the grid. Peak concurrent is the
// hero number — the one figure regulars actually trade — with a marked
// sparkline; started / now / average drop to a mono list underneath.
//
// The face "inverts to dark": browse-text-primary becomes the SURFACE and
// text steps down through white/alpha, so the reveal reads as a different
// surface, not a second card. No new color tokens needed (raw hex fails
// the designTokens jest check; white/alpha utilities are not hex).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IconX } from '@tabler/icons';
import { formatElapsed, formatLocalTime } from './format';

/**
 * Peak (value + when) and mean of the wire series.
 * @param {?Array<{viewer_count: number, timestamp: string}>} series
 * @returns {?{peak: number, peakAt: string, avg: number}} null when empty
 */
export function sessionSummary(series) {
  if (!series || series.length === 0) return null;
  let peak = series[0];
  let sum = 0;
  for (const row of series) {
    sum += row.viewer_count;
    if (row.viewer_count > peak.viewer_count) peak = row;
  }
  return {
    peak: peak.viewer_count,
    peakAt: peak.timestamp,
    avg: Math.round(sum / series.length),
  };
}

/**
 * Last-15-minutes change from the wire series (same client-derivation
 * precedent as peak/avg): delta vs the sample ~15 min back, with a percent
 * when the base is meaningful. Justifies the Rising rank in lay terms — the
 * z itself is dashboard vocabulary and never rendered here.
 * @returns {?{delta: number, pct: ?number}} null when under ~16 min of data
 */
export function recentTrend(series, windowMin = 15) {
  if (!series || series.length < windowMin + 1) return null;
  const nowRow = series[series.length - 1];
  const thenRow = series[series.length - 1 - windowMin];
  const delta = nowRow.viewer_count - thenRow.viewer_count;
  const pct =
    thenRow.viewer_count >= 25 // S3's denominator floor: tiny bases explode %
      ? Math.round((100 * delta) / thenRow.viewer_count)
      : null;
  return { delta, pct };
}

/** Full number with separators — the hero reads "1,482", never "1.5K". */
const num = (n) => (n == null ? null : n.toLocaleString('en-US'));

const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-2 border-t border-white/10 py-1.75 first:border-t-0 first:pt-0">
    <span className="shrink-0 font-plex-mono text-10 uppercase tracking-badge-live text-white/50">
      {label}
    </span>
    <span className="ml-auto text-right font-inter-tight text-13 font-medium text-white/90">
      {children}
    </span>
  </div>
);

/**
 * Inline polyline of the series with the peak sample marked. The line
 * stretches (preserveAspectRatio none, non-scaling stroke); the peak dot is
 * an overlaid element positioned in percentages so it never distorts.
 */
const Sparkline = ({ series }) => {
  if (series.length < 2) return null;
  const counts = series.map((r) => r.viewer_count);
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  const span = max - min || 1;
  // 6% vertical padding keeps the line and dot off the box edges.
  const x = (i) => (i / (series.length - 1)) * 100;
  const y = (c) => 94 - ((c - min) / span) * 88;
  const points = series.map((r, i) => `${x(i)},${y(r.viewer_count)}`).join(' ');
  const peakIndex = counts.indexOf(max);
  return (
    <div aria-hidden="true" className="relative min-h-[40px] w-full flex-1">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="h-full w-full"
      >
        <polyline
          points={points}
          fill="none"
          vectorEffect="non-scaling-stroke"
          strokeWidth="1.5"
          className="stroke-browse-accent"
        />
      </svg>
      <span
        className="absolute h-1.25 w-1.25 -translate-x-1/2 -translate-y-1/2 rounded-full bg-browse-live"
        style={{ left: `${x(peakIndex)}%`, top: `${y(max)}%` }}
      />
    </div>
  );
};

/**
 * @param {Object} props
 * @param {number|string} props.streamId
 * @param {?number} props.viewerCount  current watchers (the NOW row)
 * @param {?string} props.startsAt  ISO instant the session started
 * @param {() => void} [props.onClose]  omitted on the watch page — no ×
 * @param {number} [props.now]  epoch ms; pass to pin the clock in tests
 * @param {string} [props.timeZone]  override for tests
 */
const SessionStats = ({
  streamId,
  viewerCount,
  startsAt,
  onClose,
  now = Date.now(),
  timeZone,
}) => {
  const [series, setSeries] = useState(null); // null = loading, [] = none

  useEffect(() => {
    let alive = true;
    axios
      .get(`/api/broadcasts/${streamId}/viewer-counts/`)
      .then((res) => alive && setSeries(Array.isArray(res.data) ? res.data : []))
      .catch(() => alive && setSeries([]));
    return () => {
      alive = false;
    };
  }, [streamId]);

  const summary = sessionSummary(series);
  const trend = recentTrend(series);
  const startedAt = formatLocalTime(startsAt, timeZone);
  const elapsed = formatElapsed(startsAt, now);

  return (
    <div className="flex h-full flex-col rounded-card bg-browse-text-primary p-3.25 text-white">
      <div className="flex items-center gap-1.75">
        <span
          aria-hidden="true"
          className="h-1.25 w-1.25 rounded-full bg-browse-live"
        />
        <span className="font-plex-mono text-10 font-medium uppercase tracking-badge-live text-white/60">
          Session stats
        </span>
        {onClose && (
          <button
            type="button"
            aria-label="Close session stats"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="ml-auto flex h-[26px] w-[26px] items-center justify-center rounded-badge text-white/60 transition-colors duration-180 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-browse-accent"
          >
            <IconX size={14} stroke={2} />
          </button>
        )}
      </div>

      {series == null ? (
        <p className="flex flex-1 items-center justify-center font-plex-mono text-11 text-white/40">
          Loading…
        </p>
      ) : summary == null ? (
        <p className="flex flex-1 items-center justify-center font-plex-mono text-11 text-white/40">
          No session data yet.
        </p>
      ) : (
        <>
          <p className="mt-2.25">
            <span className="font-inter-tight text-[26px] font-semibold leading-none">
              {num(summary.peak)}
            </span>{' '}
            <span className="font-plex-mono text-11 text-white/50">
              peak concurrent
            </span>
          </p>
          <p className="mt-1.25 font-plex-mono text-10 text-white/40">
            at {formatLocalTime(summary.peakAt, timeZone)}
          </p>
          <Sparkline series={series} />
          <div className="mt-1.25">
            <Row label="Started">
              {startedAt ?? '—'}
              {elapsed && (
                <span className="text-white/50"> · {elapsed}</span>
              )}
            </Row>
            <Row label="Now">
              {num(viewerCount) ?? '—'}
              {viewerCount != null && (
                <span className="text-white/50"> watching</span>
              )}
            </Row>
            <Row label="Session avg">{num(summary.avg)}</Row>
            {trend != null && (
              <Row label="Last 15 min">
                {`${trend.delta >= 0 ? '+' : ''}${num(trend.delta)}`}
                {trend.pct != null &&
                  ` (${trend.pct >= 0 ? '+' : ''}${trend.pct}%)`}
              </Row>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SessionStats;
